import { Temporal } from "temporal-polyfill";

/** Used only when the business unit has no timezone configured. */
const FALLBACK_TIMEZONE_ID = "America/New_York";

type TimezoneInput = { timezoneId?: string };

function resolveTimezoneId(timezoneId?: string): string {
  return timezoneId ?? FALLBACK_TIMEZONE_ID;
}

function toInstant(value: string | Date): Temporal.Instant {
  const iso = typeof value === "string" ? value : value.toISOString();
  return Temporal.Instant.from(iso);
}

/** Today's calendar date (YYYY-MM-DD) in the business unit's timezone. */
export function getTodayInTimezone({ timezoneId }: TimezoneInput): string {
  return Temporal.Now.plainDateISO(resolveTimezoneId(timezoneId)).toString();
}

/** The calendar date (YYYY-MM-DD) an instant falls on in the timezone. */
export function instantToDate({
  instant,
  timezoneId,
}: TimezoneInput & { instant: string | Date }): string {
  return toInstant(instant)
    .toZonedDateTimeISO(resolveTimezoneId(timezoneId))
    .toPlainDate()
    .toString();
}

/**
 * The server stores window ends as exclusive instants (the start of the day
 * after the last covered day), so the last covered day is the day before.
 */
export function exclusiveEndToLastDay({
  instant,
  timezoneId,
}: TimezoneInput & { instant: string | Date }): string {
  return Temporal.PlainDate.from(instantToDate({ instant, timezoneId }))
    .subtract({ days: 1 })
    .toString();
}

/** Formats a YYYY-MM-DD calendar date as "Oct 5, 2026". */
export function formatCalendarDate(date: string): string {
  return Temporal.PlainDate.from(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function isCalendarDate(value: string): boolean {
  try {
    return Temporal.PlainDate.from(value).toString() === value;
  } catch {
    return false;
  }
}

/** True when the first YYYY-MM-DD date is after the second. */
export function isDateAfter({
  date,
  other,
}: {
  date: string;
  other: string;
}): boolean {
  return Temporal.PlainDate.compare(date, other) > 0;
}
